import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBroom, faArrowsRotate, faBed, faUser, faStar, faWrench,
  faCircleCheck, faTriangleExclamation, faNoteSticky, faXmark,
  faMagnifyingGlass, faCheck, faSpinner, faPen, faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./HousekeepingManagerDashboard.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const STATUS_META: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  dirty:            { label: "Dirty",          color: "#f87171", bg: "rgba(239,68,68,0.12)",    icon: faTriangleExclamation },
  cleaning:         { label: "Cleaning",       color: "#fbbf24", bg: "rgba(251,191,36,0.12)",   icon: faBroom },
  clean:            { label: "Clean",          color: "#34d399", bg: "rgba(52,211,153,0.12)",   icon: faCircleCheck },
  inspected:        { label: "Inspected",      color: "#818cf8", bg: "rgba(99,102,241,0.12)",   icon: faStar },
  "out-of-service": { label: "Out of Service", color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.12)", icon: faWrench },
};

const PRIORITY_COLOR: Record<string, string> = {
  urgent: "#f87171",
  normal: "#94a3b8",
  low:    "#475569",
};

interface RoomTask {
  roomId: string;
  roomNumber: string;
  roomType: string | null;
  status: string;
  priority: string;
  assignedTo: string | null;
  notes: string | null;
  taskId: number | null;
  checkedAt: string | null;
  inspectedBy: string | null;
}

const STATUSES  = ["dirty", "cleaning", "clean", "inspected", "out-of-service"];
const PRIORITIES = ["urgent", "normal", "low"];
const FILTERS   = ["all", ...STATUSES];

function EditModal({
  room,
  onClose,
  onSaved,
}: {
  room: RoomTask;
  onClose: () => void;
  onSaved: (updated: Partial<RoomTask>) => void;
}) {
  const [status,     setStatus]     = useState(room.status);
  const [priority,   setPriority]   = useState(room.priority);
  const [assignedTo, setAssignedTo] = useState(room.assignedTo ?? "");
  const [notes,      setNotes]      = useState(room.notes ?? "");
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = { status, priority, assigned_to: assignedTo, notes, checked_at: new Date().toISOString() };
      if (room.taskId) {
        await axios.put(`${API}/api/housekeeping/${room.taskId}`, payload);
      } else {
        await axios.post(`${API}/api/housekeeping`, { roomid: room.roomId, ...payload });
      }
      onSaved({ status, priority, assignedTo, notes });
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hkm-modal-overlay" onClick={onClose}>
      <div className="hkm-modal" onClick={e => e.stopPropagation()}>
        <div className="hkm-modal-header">
          <h3>
            <FontAwesomeIcon icon={faPen} />
            Room {room.roomNumber} — Edit Task
          </h3>
          <button className="hkm-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {err && (
          <div className="hkm-error-banner">
            <FontAwesomeIcon icon={faTriangleExclamation} /> {err}
          </div>
        )}

        <div className="hkm-field">
          <label>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            {STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_META[s]?.label ?? s}</option>
            ))}
          </select>
        </div>

        <div className="hkm-field">
          <label>Priority</label>
          <select value={priority} onChange={e => setPriority(e.target.value)}>
            {PRIORITIES.map(p => (
              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="hkm-field">
          <label>Assigned To</label>
          <input
            value={assignedTo}
            onChange={e => setAssignedTo(e.target.value)}
            placeholder="Staff name"
          />
        </div>

        <div className="hkm-field">
          <label>Notes / Request</label>
          <textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Extra towels, broken lamp…"
          />
        </div>

        <div className="hkm-modal-footer">
          <button className="hkm-modal-save" onClick={save} disabled={saving}>
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
            {" "}Save Changes
          </button>
          <button className="hkm-modal-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function HousekeepingManagerDashboard() {
  const [rooms,      setRooms]      = useState<RoomTask[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState("all");
  const [search,     setSearch]     = useState("");
  const [modal,      setModal]      = useState<RoomTask | null>(null);
  const [toast,      setToast]      = useState("");
  const [err,        setErr]        = useState("");
  const [resetTime,  setResetTime]  = useState("09:00");
  const [editReset,  setEditReset]  = useState(false);
  const [resetInput, setResetInput] = useState("09:00");
  const [resetting,  setResetting]  = useState(false);

  const token   = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [roomsRes, cfgRes] = await Promise.all([
        axios.get(`${API}/api/housekeeping`, { headers }),
        axios.get(`${API}/api/housekeeping/config`, { headers }).catch(() => ({ data: { resetTime: "09:00" } })),
      ]);
      setRooms(roomsRes.data || []);
      const rt = cfgRes.data?.resetTime ?? "09:00";
      setResetTime(rt);
      setResetInput(rt);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? "Failed to load rooms");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stats
  const counts = rooms.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const urgent = rooms.filter(r => r.priority === "urgent").length;
  const unassigned = rooms.filter(r => !r.assignedTo).length;

  // Filtered list
  const list = rooms.filter(r => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        String(r.roomNumber).toLowerCase().includes(q) ||
        (r.roomType ?? "").toLowerCase().includes(q) ||
        (r.assignedTo ?? "").toLowerCase().includes(q) ||
        (r.notes ?? "").toLowerCase().includes(q)
      );
    }
    return true;
  }).sort((a, b) =>
    String(a.roomNumber ?? "").localeCompare(String(b.roomNumber ?? ""), undefined, { numeric: true })
  );

  const onSaved = (roomId: string, updated: Partial<RoomTask>) => {
    setRooms(prev => prev.map(r => r.roomId === roomId ? { ...r, ...updated } : r));
    showToast("Room task updated");
    load();
  };

  const saveResetTime = async () => {
    if (!/^\d{2}:\d{2}$/.test(resetInput)) return;
    try {
      await axios.put(`${API}/api/housekeeping/reset-time`, { resetTime: resetInput }, { headers });
      setResetTime(resetInput);
      setEditReset(false);
      showToast(`Daily reset time set to ${resetInput}`);
    } catch { showToast("Failed to save reset time"); }
  };

  const forceReset = async () => {
    if (!confirm("Reset ALL rooms to Dirty now and mark today's arrivals as Not Ready?")) return;
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/api/housekeeping/force-reset`, {}, { headers });
      showToast(`Reset done — ${data.reset} rooms marked dirty`);
      load();
    } catch { showToast("Reset failed"); }
    finally { setResetting(false); }
  };

  return (
    <DashboardLayout>
      <div className="hkm-root">

        {/* Header */}
        <div className="hkm-header">
          <div className="hkm-header-left">
            <h1>
              <FontAwesomeIcon icon={faClipboardList} />
              Housekeeping Management
            </h1>
            <p className="hkm-date">
              {new Date().toLocaleDateString("en-GB", {
                weekday: "long", day: "2-digit", month: "long", year: "numeric",
              })}
            </p>
          </div>
          <div className="hkm-header-actions">
            {/* Daily reset time */}
            <div className="hkm-reset-block">
              <span className="hkm-reset-label">Daily Reset</span>
              {editReset ? (
                <>
                  <input
                    type="time"
                    value={resetInput}
                    onChange={e => setResetInput(e.target.value)}
                    className="hkm-reset-input"
                  />
                  <button className="hkm-btn-save-time" onClick={saveResetTime}>Save</button>
                  <button className="hkm-btn-cancel-time" onClick={() => { setEditReset(false); setResetInput(resetTime); }}>✕</button>
                </>
              ) : (
                <>
                  <span className="hkm-reset-time">{resetTime}</span>
                  <button className="hkm-btn-edit-time" onClick={() => setEditReset(true)}>
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                </>
              )}
            </div>
            <button className="hkm-btn-danger" onClick={forceReset} disabled={resetting} title="Reset all rooms to Dirty now">
              <FontAwesomeIcon icon={faArrowsRotate} spin={resetting} /> Reset Now
            </button>
            <button className="hkm-btn-secondary" onClick={load}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Refresh
            </button>
          </div>
        </div>

        {err && (
          <div className="hkm-error-banner">
            <FontAwesomeIcon icon={faTriangleExclamation} /> {err}
          </div>
        )}

        {/* Stats */}
        <div className="hkm-stats">
          {Object.entries(STATUS_META).map(([key, m]) => (
            <div
              key={key}
              className="hkm-stat"
              style={{ borderColor: (counts[key] || 0) > 0 ? `${m.color}33` : undefined, cursor: "pointer" }}
              onClick={() => setFilter(filter === key ? "all" : key)}
            >
              <span className="hkm-stat-label">{m.label}</span>
              <span className="hkm-stat-value" style={{ color: m.color }}>{counts[key] || 0}</span>
              <span className="hkm-stat-sub">rooms</span>
            </div>
          ))}
          <div className="hkm-stat" style={{ borderColor: urgent > 0 ? "rgba(239,68,68,0.3)" : undefined }}>
            <span className="hkm-stat-label">Urgent</span>
            <span className="hkm-stat-value" style={{ color: urgent > 0 ? "#f87171" : "#475569" }}>{urgent}</span>
            <span className="hkm-stat-sub">priority</span>
          </div>
          <div className="hkm-stat">
            <span className="hkm-stat-label">Unassigned</span>
            <span className="hkm-stat-value" style={{ color: unassigned > 0 ? "#fbbf24" : "#475569" }}>{unassigned}</span>
            <span className="hkm-stat-sub">rooms</span>
          </div>
          <div className="hkm-stat">
            <span className="hkm-stat-label">Total Rooms</span>
            <span className="hkm-stat-value" style={{ color: "#e2e8f0" }}>{rooms.length}</span>
            <span className="hkm-stat-sub">in property</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="hkm-toolbar">
          <div className="hkm-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: "rgba(242,235,217,0.45)" }} />
            <input
              placeholder="Search room, type, staff, notes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <FontAwesomeIcon
                icon={faXmark}
                style={{ color: "rgba(242,235,217,0.45)", cursor: "pointer" }}
                onClick={() => setSearch("")}
              />
            )}
          </div>
          {FILTERS.map(f => (
            <button
              key={f}
              className={`hkm-filter-pill${filter === f ? " active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "All" : STATUS_META[f]?.label ?? f}
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "rgba(242,235,217,0.45)" }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 26, marginBottom: 10 }} /><br />
            <span style={{ fontSize: 13 }}>Loading…</span>
          </div>
        ) : list.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "rgba(242,235,217,0.45)" }}>
            <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: 32, color: "#34d399", marginBottom: 10 }} /><br />
            <span style={{ fontSize: 14 }}>No rooms match your filter.</span>
          </div>
        ) : (
          <div className="hkm-section">
            <p className="hkm-section-title">
              <FontAwesomeIcon icon={faBed} /> {list.length} room{list.length !== 1 ? "s" : ""}
            </p>
            <div className="hkm-table-wrap">
              <table className="hkm-table">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Assigned To</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(room => {
                    const meta = STATUS_META[room.status] ?? STATUS_META.dirty;
                    return (
                      <tr key={room.roomId}>
                        <td>
                          <div className="hkm-room-num">
                            <FontAwesomeIcon icon={faBed} style={{ color: "rgba(242,235,217,0.45)", marginRight: 7, fontSize: 12 }} />
                            {room.roomNumber}
                          </div>
                          {room.roomType && <div className="hkm-room-type">{room.roomType}</div>}
                        </td>

                        <td>
                          <span
                            className="hkm-badge"
                            style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}44` }}
                          >
                            <FontAwesomeIcon icon={meta.icon} />
                            {meta.label}
                          </span>
                        </td>

                        <td>
                          <span style={{ fontSize: 12, fontWeight: 700, color: PRIORITY_COLOR[room.priority] ?? "#94a3b8" }}>
                            <span
                              className="hkm-priority-dot"
                              style={{ background: PRIORITY_COLOR[room.priority] ?? "#94a3b8" }}
                            />
                            {room.priority.charAt(0).toUpperCase() + room.priority.slice(1)}
                          </span>
                        </td>

                        <td>
                          <span style={{ fontSize: 12, color: room.assignedTo ? "#94a3b8" : "#475569", display: "flex", alignItems: "center", gap: 5 }}>
                            <FontAwesomeIcon icon={faUser} style={{ fontSize: 11 }} />
                            {room.assignedTo || "Unassigned"}
                          </span>
                        </td>

                        <td>
                          {room.notes ? (
                            <span style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                              <FontAwesomeIcon icon={faNoteSticky} style={{ marginRight: 5, color: "rgba(242,235,217,0.45)" }} />
                              {room.notes}
                            </span>
                          ) : (
                            <span style={{ color: "#2d3748", fontSize: 12 }}>—</span>
                          )}
                        </td>

                        <td>
                          <button
                            className="hkm-action-btn"
                            style={{ borderColor: "rgba(99,102,241,0.3)", color: "#818cf8" }}
                            onClick={() => setModal(room)}
                          >
                            <FontAwesomeIcon icon={faPen} /> Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {modal && (
          <EditModal
            room={modal}
            onClose={() => setModal(null)}
            onSaved={updated => { onSaved(modal.roomId, updated); setModal(null); }}
          />
        )}

        {/* Toast */}
        {toast && <div className="hkm-toast">{toast}</div>}

      </div>
    </DashboardLayout>
  );
}
